export enum ErrorType {
  FILE_TYPE = "FILE_TYPE",
  FILE_SIZE = "FILE_SIZE",
  NETWORK = "NETWORK",
  SERVER = "SERVER",
  AUTHENTICATION = "AUTHENTICATION",
  VALIDATION = "VALIDATION",
  UNKNOWN = "UNKNOWN",
}

export enum ErrorSeverity {
  CRITICAL = "CRITICAL", // Don't auto-dismiss
  WARNING = "WARNING", // Auto-dismiss after 7s
  INFO = "INFO", // Auto-dismiss after 5s
}

export interface UploadError {
  type: ErrorType;
  severity: ErrorSeverity;
  title: string;
  message: string;
  suggestions: string[];
  actions: ErrorAction[];
  icon: string;
  autoDismiss: boolean;
  dismissAfter?: number; // milliseconds
}

export enum ErrorAction {
  RETRY = "RETRY",
  CLEAR_FILE = "CLEAR_FILE",
  TRY_DIFFERENT_FILE = "TRY_DIFFERENT_FILE",
  REFRESH_PAGE = "REFRESH_PAGE",
  CONTACT_SUPPORT = "CONTACT_SUPPORT",
}

export const parseUploadError = (error: any): UploadError => {
  // File type validation error
  if (
    error?.message?.includes("file type") ||
    error?.message?.includes("not supported")
  ) {
    return {
      type: ErrorType.FILE_TYPE,
      severity: ErrorSeverity.WARNING,
      title: "error.fileType.title",
      message: "error.fileType.message",
      suggestions: ["error.fileType.suggestion1", "error.fileType.suggestion2"],
      actions: [ErrorAction.TRY_DIFFERENT_FILE, ErrorAction.CLEAR_FILE],
      icon: "📄",
      autoDismiss: true,
      dismissAfter: 7000,
    };
  }

  // File size error
  if (
    error?.message?.includes("larger") ||
    error?.message?.includes("size") ||
    error?.message?.includes("6MB")
  ) {
    return {
      type: ErrorType.FILE_SIZE,
      severity: ErrorSeverity.WARNING,
      title: "error.fileSize.title",
      message: "error.fileSize.message",
      suggestions: ["error.fileSize.suggestion1", "error.fileSize.suggestion2"],
      actions: [ErrorAction.TRY_DIFFERENT_FILE, ErrorAction.CLEAR_FILE],
      icon: "⚠️",
      autoDismiss: true,
      dismissAfter: 7000,
    };
  }

  // Network timeout
  if (
    error?.response?.status === 504 ||
    error?.code === "ECONNABORTED" ||
    error?.message?.includes("timeout")
  ) {
    return {
      type: ErrorType.NETWORK,
      severity: ErrorSeverity.WARNING,
      title: "error.network.timeout.title",
      message: "error.network.timeout.message",
      suggestions: [
        "error.network.timeout.suggestion1",
        "error.network.timeout.suggestion2",
        "error.network.timeout.suggestion3",
      ],
      actions: [ErrorAction.RETRY, ErrorAction.CLEAR_FILE],
      icon: "🌐",
      autoDismiss: false,
    };
  }

  // Network connection error
  if (
    error?.code === "ERR_NETWORK" ||
    error?.message?.includes("Network Error")
  ) {
    return {
      type: ErrorType.NETWORK,
      severity: ErrorSeverity.CRITICAL,
      title: "error.network.connection.title",
      message: "error.network.connection.message",
      suggestions: [
        "error.network.connection.suggestion1",
        "error.network.connection.suggestion2",
      ],
      actions: [ErrorAction.RETRY],
      icon: "🌐",
      autoDismiss: false,
    };
  }

  // Authentication errors
  if (error?.response?.status === 401 || error?.response?.status === 403) {
    return {
      type: ErrorType.AUTHENTICATION,
      severity: ErrorSeverity.CRITICAL,
      title: "error.authentication.title",
      message: "error.authentication.message",
      suggestions: [
        "error.authentication.suggestion1",
        "error.authentication.suggestion2",
      ],
      actions: [ErrorAction.REFRESH_PAGE],
      icon: "🔒",
      autoDismiss: false,
    };
  }

  // Server validation errors
  if (error?.response?.status === 400) {
    const apiMessage = error?.response?.data?.message || "";
    return {
      type: ErrorType.VALIDATION,
      severity: ErrorSeverity.WARNING,
      title: "error.validation.title",
      message: apiMessage || "error.validation.message",
      suggestions: [
        "error.validation.suggestion1",
        "error.validation.suggestion2",
      ],
      actions: [ErrorAction.TRY_DIFFERENT_FILE, ErrorAction.CLEAR_FILE],
      icon: "❌",
      autoDismiss: true,
      dismissAfter: 7000,
    };
  }

  // Server errors
  if (error?.response?.status >= 500) {
    return {
      type: ErrorType.SERVER,
      severity: ErrorSeverity.CRITICAL,
      title: "error.server.title",
      message: "error.server.message",
      suggestions: ["error.server.suggestion1", "error.server.suggestion2"],
      actions: [ErrorAction.RETRY, ErrorAction.CONTACT_SUPPORT],
      icon: "🔧",
      autoDismiss: false,
    };
  }

  // Generic/Unknown error
  const apiMessage = error?.response?.data?.message || error?.message || "";
  return {
    type: ErrorType.UNKNOWN,
    severity: ErrorSeverity.WARNING,
    title: "error.unknown.title",
    message: apiMessage || "error.unknown.message",
    suggestions: ["error.unknown.suggestion1", "error.unknown.suggestion2"],
    actions: [ErrorAction.RETRY, ErrorAction.CLEAR_FILE],
    icon: "ℹ️",
    autoDismiss: true,
    dismissAfter: 7000,
  };
};
